import { defineComponent as _defineComponent } from 'vue'
import { sanitize } from 'dompurify';
import { computed, ref, watch } from 'vue';
import { Route } from 'vue-router';

import { CollectionType } from '@/enums/collection-type';
import { Viewport } from '@/enums/viewport';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { getViewport } from '@/helpers/ui-helper';
import { useRoutingStore, useSearchSuggesterStore } from '@/helpers/composables/useStores';
import { useI18n } from '@/helpers/composables/useI18n';
import { useMounted } from '@vueuse/core';

import SearchBar from '@/components/search/SearchBar.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'SearchSuggesterBar',
  props: {
    collectionType: null
  },
  emits: ["ionCancel"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'ionCancel'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	collectionType: CollectionType;
};

const SearchSuggesterResults = () =>
	import(
		/* webpackChunkName: "search-suggester-results" */ '@/components/search/SearchSuggesterResults/SearchSuggesterResults.vue'
	);




const isMounted = useMounted();
const inputQuery = ref('');
const searchbar = ref<InstanceType<typeof SearchBar> | null>(null);

const searchResults = ref<{ $children?: any[] } | null>(null);
const index = ref(-1);

const { t } = useI18n();
const { activeRoute } = useRoutingStore();
const { isSearchSuggesterOpen, query, search, goToSearch, setSearchSuggesterVisibility } = useSearchSuggesterStore();

const viewport = computed<string>(() => getViewport());

const placeholder = computed(() =>
	viewport.value === Viewport.DESKTOP ? t('WEBAPP_SEARCH_PLACEHOLDER') : t('WEBAPP_SEARCH')
);

watch(
	() => props.collectionType,
	() => {
		if (props.collectionType !== CollectionType.SEARCH) {
			emits('ionCancel');
		}
	}
);

watch(query, () => {
	if (!isSearchSuggesterOpen.value && !inputQuery.value) {
		inputQuery.value = query.value;
	}
});

watch(activeRoute, (route: Route) => {
	closeSearchBar(false, true);

	// if the route has changed to a persons page, replace the input with the proper name of the person
	if (route.query.person_id && searchBarRef.value) {
		searchBarRef.value.value = route.query.q as string;
	}
});
/**
 * close results when the route has been changed.
 */

async function openSearchBar(evt: any) {
	// INP case: artificially give the JS thread time to breathe by adding a re-render.
	await new Promise(resolve => setTimeout(resolve, 0));

	if (!isSearchSuggesterOpen.value) {
		const userInput = sanitize(evt.target.value);

		search({ searchQuery: userInput });
		setSearchSuggesterVisibility(true);
	}
}

function closeSearchBar(click: boolean = false, forceClose: boolean = false) {
	// if user is on search, we want to keep the search bar
	//if user navigates to title page from search, close
	if (props.collectionType !== CollectionType.SEARCH || forceClose) {
		emits('ionCancel');
		setSearchSuggesterVisibility(false);
	}
}

function handleSearch(evt: any) {
	inputQuery.value = evt;
	onChange(evt);
}

function onChange(evt: any) {
	index.value = -1;

	const userInput = sanitize(evt);
	inputQuery.value = userInput;
	search({ searchQuery: inputQuery.value });
}

async function editSearch(searchTerm?: string) {
	if (searchBarRef.value == null) return;

	searchBarRef.value?.focus();
	inputQuery.value = searchTerm ?? '';

	search({ searchQuery: inputQuery.value });
}

function navigateElements(evt: KeyboardEvent) {
	const { code, target } = evt;
	const userInput = sanitize((target as HTMLInputElement).value);
	switch (code) {
		case 'Escape':
			clear('escape');
			if (!userInput.length) {
				closeSearchBar();
			}
			break;
		case 'ArrowDown':
			evt.preventDefault();
			move('down');
			break;

		case 'Enter':
		case 'Tab':
		case 'Backspace':
		case 'ControlLeft':
		case 'ControlRight':
		case 'AltLeft':
		case 'AltRight':
		case 'ShiftLeft':
		case 'ShiftRight':
		case 'OSLeft':
		case 'OSRight':
		case 'Space':
		case 'CapsLock':
		case 'Fn':
			break;
		default:
			setSearchSuggesterVisibility(true);
			return;
	}
}

function navigateResults(payload: number) {
	if (payload === 1) {
		move('down');
	} else if (payload === -1) {
		move('up');
	}
}

const searchBarRef = computed(() => searchbar.value?.$refs['searchbarInput'] as HTMLInputElement);

async function move(direction: 'up' | 'down') {
	const items = searchResultsItems.value;
	const itemsLength = items.length ?? 0;

	if (direction === 'down') {
		if (index.value + 1 >= itemsLength) {
			index.value = 0;
		} else {
			index.value++;
		}
	} else if (direction === 'up') {
		index.value--;
		if (index.value < 0) {
			searchBarRef.value?.focus();
		}
	}

	if (items) items[index.value]?.$refs['resultItem']?.focus?.();
}

const searchResultsItems = computed(() => {
	// We need this computed property to listen to inputQuery.value changes so the new searchResults chidren $refs are accessed when the DOM updates
	if (!inputQuery.value) return;

	const titleDetailLinks =
		searchResults.value?.$children?.[0]?.$children.filter((child: { $vnode: { tag: string | string[] } }) => {
			return child.$vnode.tag.includes('TitleDetailLink');
		}) ?? [];

	return titleDetailLinks.map((link: { $children: any[] }) => link.$children?.[0]);
});

function clear(source: string) {
	TrackingHelper.trackEvent('search_suggest_clear', {
		action: source,
	});
}

function navigateToSearchPage(searchQuery: string) {
	if (searchQuery.length <= 2) return;

	setSearchSuggesterVisibility(false);
	goToSearch({ searchString: searchQuery });
	TrackingHelper.trackEvent('search_suggest_more', {
		action: 'enter',
		label: searchQuery,
	});
}

return { __sfc: true,SearchSuggesterResults, props, emits, isMounted, inputQuery, searchbar, searchResults, index, t, activeRoute, isSearchSuggesterOpen, query, search, goToSearch, setSearchSuggesterVisibility, viewport, placeholder, openSearchBar, closeSearchBar, handleSearch, onChange, editSearch, navigateElements, navigateResults, searchBarRef, move, searchResultsItems, clear, navigateToSearchPage, SearchBar }
}

})